<template>
  <teleport
    v-if="backdropVisible && data.outerVisible"
    :to="teleportTo"
    :disabled="teleportDisabled"
  >
    <transition name="transition-fade" appear>
      <div
        v-if="data.innerVisible"
        class="floating-element__backdrop"
        @click.prevent.stop="$emit('click:backdrop', $event)"
      ></div>
    </transition>
  </teleport>

  <teleport
    v-if="data.outerVisible"
    ref="teleportElement"
    :to="teleportTo"
    :disabled="teleportDisabled"
  >
    <transition
      :name="transitionName"
      appear
      @before-enter="onBeforeEnter"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @enter-cancelled="onEnterCancelled"
      @before-leave="onBeforeLeave"
      @leave="onLeave"
      @after-leave="onAfterLeave"
      @leave-cancelled="onLeaveCancelled"
    >
      <component
        :is="tag"
        v-if="data.innerVisible"
        ref="el"
        class="floating-element"
        :data-dynamics="data.dynamicXOffset + ',' + data.dynamicYOffset"
        :data-translate="translate.x + ',' + translate.y"
        :style="style"
        v-bind="$attrs"
        @click.stop="emit('click', $event)"
        @pointerdown="onElementPointerDown"
        @pointermove="onElementPointerMove"
      >
        <slot :width="safeWidth" :height="safeHeight"></slot>
      </component>
    </transition>
    <div
      v-if="resizable"
      class="floating-element__resize-borders"
      :style="{ ...style, width: width + 'px', height: height + 'px' }"
    >
      <div v-if="resizableEdges.top" class="floating-element__resize-border -top" @pointerdown="onResizeBorderPointerDown($event, ['top'])"></div>
      <div v-if="resizableEdges.bottom" class="floating-element__resize-border -bottom" @pointerdown="onResizeBorderPointerDown($event, ['bottom'])"></div>
      <div v-if="resizableEdges.left" class="floating-element__resize-border -left" @pointerdown="onResizeBorderPointerDown($event, ['left'])"></div>
      <div v-if="resizableEdges.right" class="floating-element__resize-border -right" @pointerdown="onResizeBorderPointerDown($event, ['right'])"></div>
      <div v-if="resizableEdges.top && resizableEdges.left" class="floating-element__resize-corner -top-left" @pointerdown="onResizeBorderPointerDown($event, ['top', 'left'])"></div>
      <div v-if="resizableEdges.top && resizableEdges.right" class="floating-element__resize-corner -top-right" @pointerdown="onResizeBorderPointerDown($event, ['top', 'right'])"></div>
      <div v-if="resizableEdges.bottom && resizableEdges.left" class="floating-element__resize-corner -bottom-left" @pointerdown="onResizeBorderPointerDown($event, ['bottom', 'left'])"></div>
      <div v-if="resizableEdges.bottom && resizableEdges.right" class="floating-element__resize-corner -bottom-right" @pointerdown="onResizeBorderPointerDown($event, ['bottom', 'right'])"></div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  reactive,
  onMounted,
  onBeforeUnmount,
  nextTick
} from "vue";
import { onPointerDownOutside, onClickOutside } from "@core/composables/onMouseDownOutside";
import { onBeforeMount } from "vue";
import { useElementBounding, useResizeObserver, useWindowSize } from "@vueuse/core";

// eslint-disable-next-line no-undef
defineOptions({
  inheritAttrs: false
});

const props = withDefaults(
  defineProps<{
    visible?: boolean;
    x?: number | null;
    y?: number | null;
    xOffset?: number | string;
    tag?: string;

    movable?: boolean;
    resizable?: boolean;

    resizableEdges?: {
      top?: boolean;
      left?: boolean;
      bottom?: boolean;
      right?: boolean;
    };
    width?: number | null;
    height?: number | null;
    minWidth?: number;
    minHeight?: number;

    xAlignment?: "left" | "right" | "center";
    yAlignment?: "top" | "center" | "bottom";
    centerOffsetX?: number;

    dragHandleClass?: string;

    transitionName?: string;
    nonClosingElements?: HTMLElement[];
    teleportDisabled?: boolean;
    teleportTo?: string;
    backdropVisible?: boolean;
  }>(),
  {
    x: null,
    y: null,
    xAlignment: "left",
    yAlignment: "top",

    resizable: false,
    resizableEdges: () => ({ top: true, left: true, bottom: true, right: true }),
    minWidth: 10,
    minHeight: 10,

    visible: true,
    xOffset: 0,
    tag: "div",
    transitionName: "transition-popup",
    nonClosingElements: () => [],
    teleportDisabled: false,
    teleportTo: "#app",
    backdropVisible: false
  }
);
const emit = defineEmits<{
  (e: "before-enter", el: Element): void;
  (e: "enter", el: Element): void;
  (e: "after-enter", el: Element): void;
  (e: "enter-cancelled", el: Element): void;
  (e: "before-leave", el: Element): void;
  (e: "leave", el: Element): void;
  (e: "after-leave", el: Element): void;
  (e: "leave-cancelled", el: Element): void;
  (e: "update:visible", visible: boolean): void;
  (e: "click:backdrop", event: MouseEvent): void;
  (e: "click:outside", event: MouseEvent): void;
  (e: "pointerdown:outside", event: MouseEvent): void;
  (e: "move", position: { x: number; y: number, width?: number; height?: number }): void;
  (e: "resize", size: { x: number; y: number; width: number; height: number }): void;
  (e: "click", event: MouseEvent): void;
}>();

const el = ref<HTMLElement>();
const teleportElement = ref<HTMLElement>();
const data = reactive({
  dynamicXOffset: 0,
  dynamicYOffset: 0,
  localWidth: props.width,
  localHeight: props.height,

  outerVisible: false,
  innerVisible: false,
  hiding: false,
  transitionsDone: false
});
const translate = reactive({
  x: 0,
  y: 0
});

const { width, height, update: updateElementBounding } = useElementBounding(el);
const { width: windowWidth, height: windowHeight } = useWindowSize();
const safeWidth = computed(() => {
  return data.localWidth || width.value || 0;
});
const safeHeight = computed(() => {
  return data.localHeight || height.value || 0;
});

watch(() => props.width, (value) => {
  data.localWidth = value;
});
watch(() => props.height, (value) => {
  data.localHeight = value;
});
watch([() => props.x, () => props.y], () => {
  translate.x = 0;
  translate.y = 0;
});

const safeX = computed(() => {
  if (props.x != null) return props.x;
  if (props.xAlignment === "center") {
    let width = windowWidth.value;
    if (props.centerOffsetX) {
      width -= props.centerOffsetX;
    }
    return width / 2 + (props.centerOffsetX || 0);
  }
  if (props.xAlignment === "right") return windowWidth.value;
  return 0;
});
const safeY = computed(() => {
  if (props.y != null) return props.y;
  if (props.yAlignment === "center") return windowHeight.value / 2;
  if (props.yAlignment === "bottom") return windowHeight.value;
  return 0;
});

let unbindClick: (() => void) | undefined = undefined;
let unbindMousedown: (() => void) | undefined = undefined;

const positionStyle = computed(() => {
  const style: {
    left?: string;
    right?: string;
    bottom?: string;
    top?: string;
    transform?: string;
    width?: string;
    height?: string;
    cursor?: string;
  } = {};

  // SIZE

  const width = Math.max(safeWidth.value, props.minWidth);
  const height = Math.max(safeHeight.value, props.minHeight);

  if (data.localWidth != null) {
    style.width = `${width}px`;
  }
  if (data.localHeight != null) {
    style.height = `${height}px`;
  }

  // POSITION

  const x = safeX.value;
  const y = safeY.value;

  const xOffset = calcOffset(props.xOffset, width);

  if (props.xAlignment == "right") {
    style.right = `${windowWidth.value - x + xOffset}px`;
    style.left = "auto";
  } else if (props.xAlignment == "center") {
    style.left = `${x - width / 2 + xOffset}px`;
  } else {
    style.left = `${x + xOffset}px`;
  }

  const screenHeight = windowHeight.value;
  if (props.yAlignment == "bottom") {
    style.bottom = `${screenHeight - y}px`;
  } else if (props.yAlignment == "center") {
    style.top = `${y - height / 2}px`;
  } else {
    style.top = `${y}px`;
  }

  return style;
});
const transform = computed(() => {
  return `translate3d(${translate.x + data.dynamicXOffset}px, ${translate.y + data.dynamicYOffset}px, 0)`;
});
const style = computed(() => {
  const style = {
    ...positionStyle.value
  };
  if (data.dynamicYOffset != 0 || translate.y != 0 || data.dynamicXOffset != 0 || translate.x != 0) {
    style.transform = transform.value;
  }
  style.cursor = draggable.value ? "move" : "default";

  return style;
});

watch(
  () => props.visible,
  (value) => {
    updateVisibility(value);
  },
  {
    immediate: true
  }
);

onBeforeMount(() => {
  data.innerVisible = props.visible;
  data.outerVisible = props.visible;
});
onMounted(() => {
  updateVisibility(props.visible);
});
onBeforeUnmount(() => {
  if (unbindMousedown) unbindMousedown();
  if (unbindClick) unbindClick();
});

async function updateVisibility(visible: boolean) {
  if (visible) {
    data.hiding = false;
    data.outerVisible = true;

    if (data.innerVisible == false) {
      await nextTick();
      data.innerVisible = true;
      await nextTick();
    }

    if (unbindMousedown) unbindMousedown();
    unbindMousedown = onPointerDownOutside(
      [el, ...props.nonClosingElements],
      (event) => {
        emit("pointerdown:outside", event);
      }
    );
    if (unbindClick) unbindClick();
    unbindClick = onClickOutside(
      [el, ...props.nonClosingElements],
      (event) => {
        emit("click:outside", event);
      },
      { capture: false }
    );

    emit("update:visible", true);
  } else {
    if (unbindMousedown) {
      unbindMousedown();
      unbindMousedown = undefined;
    }
    data.hiding = true;
    data.innerVisible = false;
    emit("update:visible", false);
  }
}

function updateOffsets(
  force = false
) {
  if (data.transitionsDone == false && !force) return;

  limitOffsets();
}

function calcOffset(offsetString: string | number, base: number): number {
  if (typeof offsetString === "number") return offsetString;
  if (!offsetString) return 0;
  const offset = parseInt(offsetString);
  if (isNaN(offset)) return 0;
  if (offsetString.endsWith("px")) return offset;
  if (offsetString.endsWith("%")) return (base * offset) / 100;
  console.warn(
    `FloatingElement: Invalid offset value "${offsetString}". Only "px" and "%" are supported.`
  );
  return 0;
}

function onBeforeEnter(el: Element) {
  // called before the element is inserted into the DOM.
  // use this to set the "enter-from" state of the element

  data.transitionsDone = false;

  updateOffsets(true);

  emit("before-enter", el);
}
function onEnter(el: Element) {
  emit("enter", el);

  if (teleportElement.value) {
    Object.assign(el, {
      teleportParent: teleportElement.value?.parentElement
    });
  }
}
function onAfterEnter(el: Element) {
  data.transitionsDone = true;

  nextTick(() => {
    updateElementBounding();
    updateOffsets();
  });

  emit("after-enter", el);
}
function onEnterCancelled(el: Element) {
  data.transitionsDone = true;
  emit("enter-cancelled", el);
}
function onBeforeLeave(el: Element) {
  data.transitionsDone = false;
  emit("before-leave", el);
}
function onLeave(el: Element) {
  emit("leave", el);
}
function onAfterLeave(el: Element) {
  if (data.hiding) {
    data.hiding = false;
    data.outerVisible = false;
  }
  emit("after-leave", el);
}
function onLeaveCancelled(el: Element) {
  emit("leave-cancelled", el);
}

// ==============================
// DRAGGING

function onElementPointerDown(event: PointerEvent) {
  if (!props.movable) return;
  if (!props.dragHandleClass) return;
  if (!el.value) return;
  
  const path = event.composedPath() as HTMLElement[];
  const isInDragHandle = path.some((el) =>
    el.classList?.contains(props.dragHandleClass as string)
  );
  if (!isInDragHandle) return;
  event.preventDefault();
  event.stopPropagation();

  const startX = event.clientX;
  const startY = event.clientY;

  translate.x += data.dynamicXOffset;
  translate.y += data.dynamicYOffset;
  data.dynamicXOffset = 0;
  data.dynamicYOffset = 0;

  const startOffsetX = translate.x;
  const startOffsetY = translate.y;

  const bounds = el.value.getBoundingClientRect();
  if (data.localWidth != null && data.localWidth !== bounds.width) {
    data.localWidth = bounds.width;
  }
  if (data.localHeight != null && data.localHeight !== bounds.height) {
    data.localHeight = bounds.height;
  }

  function onPointerMove(e: PointerEvent) {
    translate.x = (e.clientX - startX) + startOffsetX;
    translate.y = (e.clientY - startY) + startOffsetY;

    limitOffsets();
  }

  function onPointerUp() {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);

    const endPosition = {
      x: safeX.value + translate.x,
      y: safeY.value + translate.y
    };

    emit("move", endPosition);
  }

  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
}

const draggable = ref<boolean>(false);

function onElementPointerMove(event: PointerEvent) {
  if (!props.movable) return;
  draggable.value = canDragElement(event);
}
function canDragElement(event: PointerEvent): boolean {
  if (!props.dragHandleClass) return false;
  if (!el.value) return false;

  const path = event.composedPath() as HTMLElement[];
  const isInDragHandle = path.some((el) =>
    el.classList?.contains(props.dragHandleClass as string)
  );
  return isInDragHandle;
}

// ==============================
// RESIZE

function onResizeBorderPointerDown(event: PointerEvent, directions: string[]) {
  if (!props.resizable) return;
  event.preventDefault();
  event.stopPropagation();
  if (!el.value) return;

  const startX = event.clientX;
  const startY = event.clientY;

  const bounds = el.value.getBoundingClientRect();

  const startWidth = bounds.width;
  const startHeight = bounds.height;
  const startLeft = bounds.left;
  const startTop = bounds.top;
  const startRight = bounds.right;
  const startBottom = bounds.bottom;
  const maxLeft = startRight - props.minWidth;
  const maxTop = startBottom - props.minHeight;
  const minBottom = startBottom - startHeight + props.minHeight;
  const minRight = startRight - startWidth + props.minWidth;
  const startTranslateX = translate.x;
  const startTranslateY = translate.y;

  function onPointerMove(e: PointerEvent) {
    let newWidth = startWidth;
    let newHeight = startHeight;
    let newLeft = startLeft;
    let newTop = startTop;
    let newRight = startRight;
    let newBottom = startBottom;

    const mousePosition = {
      x: Math.min(Math.max(e.clientX, 0), window.innerWidth),
      y: Math.min(Math.max(e.clientY, 0), window.innerHeight)
    };

    const deltaX = mousePosition.x - startX;
    const deltaY = mousePosition.y - startY;

    const screenHeight = window.innerHeight;

    if (directions.includes("right")) {
      newWidth = Math.max(startWidth + deltaX, props.minWidth);
      newRight = startLeft + newWidth;
      if (newRight < minRight) {
        newRight = minRight;
        newWidth = props.minWidth;
      }
      const delta = newWidth - startWidth;
      if (props.xAlignment === "center") {
        translate.x = startTranslateX + delta / 2;
      }
      data.localWidth = newWidth;
    }
    if (directions.includes("bottom")) {
      newHeight = Math.max(startHeight + deltaY, props.minHeight);
      newBottom = Math.min(startTop + newHeight, screenHeight);
      if (newBottom < minBottom) {
        newBottom = minBottom;
        newHeight = props.minHeight;
      }
      const delta = newHeight - startHeight;
      if (props.yAlignment === "center") {
        translate.y = startTranslateY + delta / 2;
      }
      data.localHeight = newHeight;
    }
    if (directions.includes("left")) {
      newWidth = Math.max(startWidth - deltaX, props.minWidth);
      newLeft = startRight - newWidth;
      if (newLeft < 0) {
        newLeft = 0;
        newWidth = startWidth + startLeft;
      }
      if (newLeft > maxLeft) {
        newLeft = maxLeft;
        newWidth = props.minWidth;
      }
      const delta = startWidth - newWidth;
      if (props.xAlignment === "center") {
        translate.x = startTranslateX + delta / 2;
      }
      data.localWidth = newWidth;
    }
    if (directions.includes("top")) {
      newHeight = Math.max(startHeight - deltaY, props.minHeight);
      newTop = startTop + deltaY;
      if (newTop < 0) {
        newTop = 0;
        newHeight = startHeight + startTop;
      }
      if (newTop > maxTop) {
        newTop = maxTop;
        newHeight = props.minHeight;
      }
      const delta = startHeight - newHeight;
      if (props.yAlignment === "center") {
        translate.y = startTranslateY + delta / 2;
      } else if (props.yAlignment === "bottom") {
        newBottom = newTop + newHeight;
        if (newBottom > screenHeight) {
          newBottom = screenHeight;
          newHeight = startHeight + (screenHeight - startBottom);
          newTop = newBottom - newHeight;
        }
      }
      data.localHeight = newHeight;
    }

    if (props.xAlignment === "right") {
      translate.x = (newRight - startRight) + startTranslateX;
    } else if (props.xAlignment === "left") {
      translate.x = (newLeft - startLeft) + startTranslateX;
    }
    if (props.yAlignment === "bottom") {
      translate.y = (newBottom - startBottom) + startTranslateY;
    } else if (props.yAlignment === "top") {
      translate.y = (newTop - startTop) + startTranslateY;
    }
  }

  function onPointerUp() {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);

    const newSize = {
      width: safeWidth.value,
      height: safeHeight.value,
      x: safeX.value + translate.x,
      y: safeY.value + translate.y
    };

    emit("resize", newSize);
  }

  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
}

// =============================
// LIMIT OFFSETS

useResizeObserver(el, (entries) => {
  for (const entry of entries) {
    if (entry.borderBoxSize && entry.borderBoxSize.length > 0) {
      const borderBox = entry.borderBoxSize[0];
      if (data.localWidth != null && data.localWidth !== borderBox.inlineSize) {
        data.localWidth = borderBox.inlineSize;
      }
      if (data.localHeight != null && data.localHeight !== borderBox.blockSize) {
        data.localHeight = borderBox.blockSize;
      }
    }
  }
  limitOffsets();
});

watch([windowWidth, windowHeight, () => props.x, () => props.y], () => {
  limitOffsets();
});

function limitOffsets() {
  const x = safeX.value + translate.x;
  const y = safeY.value + translate.y;
  const width = safeWidth.value;
  const height = safeHeight.value;

  data.dynamicXOffset = 0;
  data.dynamicYOffset = 0;

  if (props.yAlignment === "top") {
    // Limit to screen using cached height
    const maxTop = windowHeight.value - height;
    const top = y;
    if (top < 0) {
      data.dynamicYOffset = -y;
    } else if (top > maxTop) {
      data.dynamicYOffset = maxTop - y;
    }
  } else if (props.yAlignment === "bottom") {
    // Limit to screen using cached height
    const maxBottom = windowHeight.value;
    const minBottom = height;
    const bottom = y;
    const top = bottom - height;
    if (bottom > maxBottom) {
      data.dynamicYOffset = maxBottom - y;
    } else if (top < 0) {
      data.dynamicYOffset = minBottom - y;
    }
  } else if (props.yAlignment === "center") {
    const top = y - height / 2;
    const bottom = top + height;
    const maxBottom = windowHeight.value;
    if (top < 0) {
      data.dynamicYOffset = height / 2 - y;
    } else if (bottom > maxBottom) {
      data.dynamicYOffset = maxBottom - y - height / 2;
    }
  }

  const xOffset = calcOffset(props.xOffset, width);

  if (props.xAlignment === "left") {
    // Limit to screen using cached width
    const maxRight = windowWidth.value;
    const left = x + xOffset;
    const right = left + width;
    if (right > maxRight) {
      data.dynamicXOffset = maxRight - x - width - xOffset;
    } else if (left < 0) {
      data.dynamicXOffset = -x - xOffset;
    }
  } else if (props.xAlignment === "right") {
    // Limit to screen using cached width
    const maxRight = windowWidth.value;
    const right = x + xOffset;
    const left = right - width;
    if (left < 0) {
      data.dynamicXOffset = width - x - xOffset;
    } else if (right > maxRight) {
      data.dynamicXOffset = maxRight - x;
    }
  }
  else if (props.xAlignment === "center") {
    const left = x - width / 2 + xOffset;
    const right = left + width;
    const maxRight = windowWidth.value;
    if (left < 0) {
      data.dynamicXOffset = width / 2 - x - xOffset;
    } else if (right > maxRight) {
      data.dynamicXOffset = maxRight - x - width / 2 - xOffset;
    }
  }
}

defineExpose({
  el
});
</script>

<style lang="scss">
.floating-element {
  position: fixed;
  // DO NOT set z-index on this component.
  // Floating elements should be layered in order of appearance,
  // setting z-index breaks that.

  &__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-backdrop);

    // allow slow fades without blocking clicks
    &.transition-fade-leave-active {
      pointer-events: none;
    }
  }

  &__resize-borders {
    position: fixed;
    pointer-events: none;
  }
  &__resize-border {
    position: absolute;
    pointer-events: auto;
    background-color: transparent;
    &.-top {
      top: -3px;
      left: 0;
      right: 0;
      height: 6px;
      cursor: ns-resize;
    }
    &.-bottom {
      bottom: -3px;
      left: 0;
      right: 0;
      height: 6px;
      cursor: ns-resize;
    }
    &.-left {
      top: 0;
      left: -3px;
      bottom: 0;
      width: 6px;
      cursor: ew-resize;
    }
    &.-right {
      top: 0;
      right: -3px;
      bottom: 0;
      width: 6px;
      cursor: ew-resize;
    }
  }
  &__resize-corner {
    position: absolute;
    width: 8px;
    height: 8px;
    pointer-events: auto;
    background-color: transparent;
    &.-top-left {
      top: -4px;
      left: -4px;
      cursor: nwse-resize;
    }
    &.-top-right {
      top: -4px;
      right: -4px;
      cursor: nesw-resize;
    }
    &.-bottom-left {
      bottom: -4px;
      left: -4px;
      cursor: nesw-resize;
    }
    &.-bottom-right {
      bottom: -4px;
      right: -4px;
      cursor: nwse-resize;
    }
  }
}
</style>
